import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  TrendingUp, 
  FileText, 
  BarChart3, 
  Calendar,
  CheckCircle,
  Target,
  Plus,
  Video
} from 'lucide-react';
import { CheckIn, Material, AnalyticsData, Client, Subscription } from '../types';
import apiService from '../services/api';

const Dashboard: React.FC = () => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [clientProfile, setClientProfile] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Group client subscription UI state
  const [groupSubscription, setGroupSubscription] = useState<Subscription | null>(null);
  const [isGroupSubscriptionLoading, setIsGroupSubscriptionLoading] = useState(false);
  const [groupSubscriptionLoadError, setGroupSubscriptionLoadError] = useState<string | null>(null);
  const [isCancelSubscriptionOpen, setIsCancelSubscriptionOpen] = useState(false);
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
  const [cancelSubscriptionError, setCancelSubscriptionError] = useState<string | null>(null);
  const [cancelSubscriptionSuccess, setCancelSubscriptionSuccess] = useState<string | null>(null);

  const fetchTrainerAnalytics = useCallback(async () => {
    if (user?.role !== 'trainer') return;
    
    setLoading(true);
    setError(null);
    console.log('[Dashboard] Fetching trainer analytics...');
    
    try {
      const data: AnalyticsData = await apiService.getTrainerAnalytics();
      console.log('[Dashboard] Trainer analytics fetched:', data);
      setAnalytics(data);
    } catch (err: any) {
      console.error('[Dashboard] Error fetching trainer analytics:', err);
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  const fetchClientProfile = useCallback(async () => {
    if (user?.role !== 'client') return;
    
    try {
      const profile = await apiService.getCurrentClientProfile();
      setClientProfile(profile);
    } catch (err: any) {
      console.error('[Dashboard] Error fetching client profile:', err);
      // Don't set error, just continue without profile
    }
  }, [user?.role]);

  const fetchGroupSubscription = useCallback(async (clientId: number) => {
    setIsGroupSubscriptionLoading(true);
    setGroupSubscriptionLoadError(null);
    try {
      const raw = await apiService.getSubscriptions(1, clientId, undefined, true);

      // Backends in this repo sometimes return either:
      // - a raw list: Subscription[]
      // - a paginated object: { results: Subscription[] }
      // - a wrapped success response: { success: true, data: ... }
      const normalizedPayload: any = (raw as any)?.success ? (raw as any)?.data : raw;
      const subscriptions: Subscription[] = Array.isArray(normalizedPayload)
        ? normalizedPayload
        : Array.isArray(normalizedPayload?.results)
          ? normalizedPayload.results
          : [];

      const activeSub = subscriptions?.[0] || null;
      setGroupSubscription(activeSub);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Failed to load subscription status.';
      setGroupSubscriptionLoadError(msg);
      setGroupSubscription(null);
    } finally {
      setIsGroupSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('[Dashboard] useEffect fired');
    console.log('[Dashboard] isLoading:', isLoading);
    console.log('[Dashboard] user:', user);
    console.log('[Dashboard] isAuthenticated:', isAuthenticated);
    
    // Only proceed if auth check is complete and user is authenticated
    if (!isLoading && isAuthenticated && user) {
      if (user.role === 'trainer') {
        fetchTrainerAnalytics();
      } else if (user.role === 'client') {
        fetchClientProfile();
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [isLoading, isAuthenticated, user, fetchTrainerAnalytics, fetchClientProfile]);

  useEffect(() => {
    if (user?.role !== 'client') return;
    if (!clientProfile?.id) return;
    if (clientProfile.category !== 'group') return;
    fetchGroupSubscription(clientProfile.id);
  }, [user?.role, clientProfile?.id, clientProfile?.category, fetchGroupSubscription]);

  // Redirect if not authenticated or user is missing
  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !user)) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  // Show loading while auth is being checked
  if (isLoading) {
    return <div>Loading authentication...</div>;
  }

  // Defensive: don't render dashboard if user is missing
  if (!user) {
    return null;
  }

  // Show loading while fetching dashboard data
  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  console.log('[Dashboard] Render analytics:', analytics);

  const TrainerDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Clients</dt>
                <dd className="text-lg font-medium text-gray-900">{analytics?.total_clients || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Active Subscriptions</dt>
                <dd className="text-lg font-medium text-gray-900">{analytics?.active_subscriptions || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Monthly Revenue</dt>
                <dd className="text-lg font-medium text-gray-900">€{analytics?.monthly_revenue || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-purple-500" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Check-in Rate</dt>
                <dd className="text-lg font-medium text-gray-900">{analytics?.check_in_completion_rate || 0}%</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ClientDashboard = () => {
    const isGroupClient = clientProfile?.category === 'group';

    const handleOpenCancelSubscription = () => {
      setCancelSubscriptionError(null);
      setCancelSubscriptionSuccess(null);
      setIsCancelSubscriptionOpen(true);
    };

    const handleCloseCancelSubscription = () => {
      if (isCancelingSubscription) return;
      setIsCancelSubscriptionOpen(false);
    };

    const handleConfirmCancelSubscription = async () => {
      if (!clientProfile?.id) return;
      if (!groupSubscription?.id) {
        setCancelSubscriptionError('No active subscription found for your account.');
        return;
      }

      setIsCancelingSubscription(true);
      setCancelSubscriptionError(null);
      setCancelSubscriptionSuccess(null);

      try {
        await apiService.cancelSubscription(groupSubscription.id, {
          cancel_at_period_end: true,
          reason: 'Client requested cancellation (no refund)',
        });

        setCancelSubscriptionSuccess(
          'Subscription canceled. You’ll keep access until the end of your current billing period.'
        );
        setIsCancelSubscriptionOpen(false);
        await fetchGroupSubscription(clientProfile.id);
      } catch (err: any) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to cancel subscription. Please try again.';
        setCancelSubscriptionError(msg);
      } finally {
        setIsCancelingSubscription(false);
      }
    };
    
    return (
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="card bg-gradient-to-r from-primary to-primary-dark">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white">
              Welcome back, {user?.first_name || user?.username}!
            </h2>
            <p className="mt-2 text-white/90">
              {isGroupClient 
                ? 'Ready to crush your fitness goals with your group?'
                : 'Ready to crush your fitness goals today?'}
            </p>
          </div>
        </div>

        {/* Group Client Specific Features */}
        {isGroupClient && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div 
              className="card hover:shadow-md transition-shadow cursor-pointer border-2 border-blue-500"
              onClick={() => navigate('/group-training-plans')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/group-training-plans')}
              tabIndex={0}
              role="button"
              aria-label="View training plans"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Target className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Training Plans</h3>
                  <p className="text-sm text-gray-500">View and download your latest plan</p>
                </div>
              </div>
            </div>

            <div 
              className="card hover:shadow-md transition-shadow cursor-pointer border-2 border-purple-500"
              onClick={() => navigate('/group-zoom-sessions')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/group-zoom-sessions')}
              tabIndex={0}
              role="button"
              aria-label="View zoom sessions"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Video className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Zoom Sessions</h3>
                  <p className="text-sm text-gray-500">Join monthly group meetings</p>
                </div>
              </div>
            </div>

            <div
              className="card hover:shadow-md transition-shadow cursor-pointer border-2 border-green-500"
              onClick={() => navigate('/materials')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/materials')}
              tabIndex={0}
              role="button"
              aria-label="View training materials"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Group Guides</h3>
                  <p className="text-sm text-gray-500">Access group resources and PDFs</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Group Subscription Management */}
        {isGroupClient && (
          <div className="card border border-red-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Subscription</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Cancel anytime. There is no refund. If you cancel, you’ll keep access until the end of your current billing period.
                </p>

                {isGroupSubscriptionLoading ? (
                  <p className="mt-3 text-sm text-gray-500">Loading subscription status…</p>
                ) : groupSubscription ? (
                  <div className="mt-3 text-sm text-gray-700 space-y-1">
                    <p>
                      <span className="font-medium">Status:</span> {groupSubscription.status}
                      {groupSubscription.cancel_at_period_end ? ' (cancels at period end)' : ''}
                    </p>
                    <p>
                      <span className="font-medium">Current period ends:</span>{' '}
                      {new Date(groupSubscription.current_period_end).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-700">
                    We couldn’t find an active subscription for your account.
                  </p>
                )}

                {groupSubscriptionLoadError && (
                  <p className="mt-3 text-sm text-red-700" role="alert">
                    {groupSubscriptionLoadError}
                  </p>
                )}

                {cancelSubscriptionError && (
                  <p className="mt-3 text-sm text-red-700" role="alert">
                    {cancelSubscriptionError}
                  </p>
                )}
                {cancelSubscriptionSuccess && (
                  <p className="mt-3 text-sm text-green-700" role="status">
                    {cancelSubscriptionSuccess}
                  </p>
                )}
              </div>

              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={handleOpenCancelSubscription}
                  disabled={!groupSubscription || isGroupSubscriptionLoading || isCancelingSubscription || groupSubscription.cancel_at_period_end}
                  className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 disabled:opacity-50"
                  aria-label="Cancel subscription"
                >
                  {groupSubscription?.cancel_at_period_end ? 'Cancellation scheduled' : 'Cancel subscription'}
                </button>
              </div>
            </div>

            {/* Confirmation modal */}
            {isCancelSubscriptionOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cancel-subscription-title"
                aria-describedby="cancel-subscription-description"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCloseCancelSubscription();
                }}
                tabIndex={-1}
              >
                <div
                  className="absolute inset-0 bg-black/50"
                  onClick={handleCloseCancelSubscription}
                  aria-hidden="true"
                />
                <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
                  <h4 id="cancel-subscription-title" className="text-lg font-semibold text-gray-900">
                    Cancel your subscription?
                  </h4>
                  <p id="cancel-subscription-description" className="mt-2 text-sm text-gray-700">
                    This will cancel your subscription with no refund. You’ll keep access until the end of your current billing period.
                  </p>

                  <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={handleCloseCancelSubscription}
                      disabled={isCancelingSubscription}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      aria-label="Keep subscription"
                    >
                      Keep subscription
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmCancelSubscription}
                      disabled={isCancelingSubscription}
                      className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      aria-label="Confirm cancel subscription"
                    >
                      {isCancelingSubscription ? 'Canceling…' : 'Confirm cancellation'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions - Only show for 1-on-1 clients */}
        {!isGroupClient && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              className="card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/checkin')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/checkin')}
              tabIndex={0}
              role="button"
              aria-label="Go to weekly check-in form"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-primary" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Weekly Check-in</h3>
                  <p className="text-sm text-gray-500">Submit your progress update</p>
                </div>
              </div>
            </div>

            <div 
              className="card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/progress')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/progress')}
              tabIndex={0}
              role="button"
              aria-label="View progress tracking"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">View Progress</h3>
                  <p className="text-sm text-gray-500">Track your fitness journey</p>
                </div>
              </div>
            </div>

            <div 
              className="card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/materials')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/materials')}
              tabIndex={0}
              role="button"
              aria-label="Access training materials"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Target className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Training Materials</h3>
                  <p className="text-sm text-gray-500">Access your programs</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Dashboard
            </h1>
            <p className="mt-2 text-gray-600">
              {user?.role === 'trainer' 
                ? 'Overview of your coaching business and client progress'
                : 'Track your fitness progress and access your training materials'
              }
            </p>
          </div>
          {user?.role === 'trainer' && (
            <button
              onClick={() => navigate('/checkins/questions')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Check In Form
            </button>
          )}
        </div>
      </div>

      {user?.role === 'trainer' ? <TrainerDashboard /> : <ClientDashboard />}
    </div>
  );
};

export default Dashboard; 